import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Target, AlertCircle, CheckCircle, Clock, DollarSign, Shield, Calendar, RefreshCw, ExternalLink, ChevronDown, ChevronUp, Plus, Minus, RefreshCcw, X } from 'lucide-react';
import { AIValidatedInputWithHighlight, ValueStatus } from './AIValidatedInputWithHighlight';
import { LoadingSpinner } from './LoadingSpinner';
import { PDFViewerModal } from './PDFViewerModal';
import { useAuth0 } from '@auth0/auth0-react';

interface ExtractedTerm {
  id: string;
  confidence_score: number;
  source_text: string;
  source_start_char?: number;
  source_end_char?: number;
  extraction_note?: string;
  validation_status: string;
  status?: 'new' | 'unchanged' | 'superseded' | 'removed';
  superseded_by_term_id?: string;
  supersedes_term_id?: string;
}

interface SLATerm extends ExtractedTerm {
  sla_type: string;
  sla_name: string;
  sla_value: string;
  target_percentage?: number;
  time_value?: number;
  time_unit?: string;
  penalty_description?: string;
  penalty_amount?: number;
  penalty_currency?: string;
}

interface PerformanceGuarantee extends ExtractedTerm {
  pg_type: string;
  pg_name: string;
  pg_description: string;
  performance_metric: string;
  target_value: string;
  target_percentage?: number;
  measurement_period?: string;
  measurement_frequency?: string;
  penalty_description?: string;
  penalty_amount?: number;
  penalty_currency?: string;
  penalty_calculation_method?: string;
  remedy_description?: string;
  escalation_procedure?: string;
  reporting_requirements?: string;
  baseline_period?: string;
  improvement_target?: string;
}

interface CommercialTerm extends ExtractedTerm {
  term_type: string;
  term_name: string;
  term_value: string;
  monetary_amount?: number;
  currency?: string;
  percentage?: number;
}

interface RiskTerm extends ExtractedTerm {
  risk_type: string;
  risk_category: string;
  risk_name: string;
  risk_description: string;
  risk_severity?: string;
  risk_probability?: string;
  mitigation_measures?: string;
  insurance_required?: boolean;
  insurance_amount?: number;
  insurance_currency?: string;
  liability_cap_amount?: number;
  liability_cap_currency?: string;
  liability_cap_description?: string;
  termination_trigger?: string;
  notice_period_days?: number;
}

interface DateSigningTerm extends ExtractedTerm {
  date_type: string;
  date_name: string;
  date_text?: string;
  is_approximate?: boolean;
  duration_value?: number;
  duration_unit?: string;
  duration_relative_to?: string;
  signing_requirement?: string;
  signature_type?: string;
  witness_required?: boolean;
  notarization_required?: boolean;
}

interface DocumentAnalysisResult {
  document_id: string;
  session_id: string;
  commercial_terms: CommercialTerm[];
  sla_terms: SLATerm[];
  performance_guarantees: PerformanceGuarantee[];
  date_signing_terms: DateSigningTerm[];
  risk_terms: RiskTerm[];
  key_terms?: any[];
}

interface ExtractedTermsDisplayProps {
  documentId: string;
  documentText?: string;
  documentUrl?: string; // URL to the PDF document
  documentFilename?: string;
  onTermUpdate?: (termId: string, newValue: string, status: ValueStatus) => void;
}

export const ExtractedTermsDisplay: React.FC<ExtractedTermsDisplayProps> = ({
  documentId,
  documentText = '',
  documentUrl,
  documentFilename = 'document.pdf',
  onTermUpdate
}) => {
  const [analysisResult, setAnalysisResult] = useState<DocumentAnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
  const [pdfSearchText, setPDFSearchText] = useState('');
  const [activeTab, setActiveTab] = useState<'keyTerms' | 'insights'>('keyTerms');
  const [selectedKeyTermTags, setSelectedKeyTermTags] = useState<string[]>([]);
  const [selectedInsightTags, setSelectedInsightTags] = useState<string[]>([]);
  const [collapsedTerms, setCollapsedTerms] = useState<Set<string>>(new Set());

  // Friendly label lookups for tags
  const KEY_TERM_TAG_LABELS: Record<string, string> = {
    commercial: 'Commercial',
    date_signing: 'Signature Dates',
    sla: 'SLA',
    performance_guarantee: 'Performance Guarantees',
    risk: 'Risk',
  };

  const INSIGHT_TAG_LABELS: Record<string, string> = {
    risk: 'Risks',
  };

  const formatTagLabel = (key: string, map: Record<string, string>) => {
    if (map[key]) return map[key];
    // Fallback: convert snake_case to Title Case
    return key
      .split('_')
      .map((s) => (s ? s[0].toUpperCase() + s.slice(1) : ''))
      .join(' ');
  };
  const { getAccessTokenSilently } = useAuth0();

  const fetchAnalysisResults = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email"
        }
      });

      const response = await fetch(`/api/v1/documents/${documentId}/analysis`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analysis results');
      }

      const data = await response.json();
      setAnalysisResult(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [documentId, getAccessTokenSilently]);

  useEffect(() => {
    fetchAnalysisResults();
  }, [fetchAnalysisResults]);

  // Initialize collapsed state for removed terms
  useEffect(() => {
    if (analysisResult?.key_terms) {
      const removedTermIds = new Set(
        analysisResult.key_terms
          .filter((term: any) => term.status === 'removed')
          .map((term: any) => term.id || `term-${Math.random()}`)
      );
      setCollapsedTerms(removedTermIds);
    }
  }, [analysisResult]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalysisResults();
  };

  const toggleTermCollapse = (termId: string) => {
    setCollapsedTerms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(termId)) {
        newSet.delete(termId);
      } else {
        newSet.add(termId);
      }
      return newSet;
    });
  };

  const getStatusBadge = (status?: string) => {
    if (!status) return null;

    const badges = {
      new: { icon: Plus, color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', label: 'New' },
      unchanged: { icon: Minus, color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400', label: 'Unchanged' },
      superseded: { icon: RefreshCcw, color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Superseded' },
      removed: { icon: X, color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', label: 'Removed' }
    };

    const badge = badges[status as keyof typeof badges];
    if (!badge) return null;

    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const handleGoToPage = (sourceText: string) => {
    if (!documentUrl) {
      alert('Document PDF not available for viewing');
      return;
    }
    
    // Extract a meaningful search query from the source text
    let searchQuery = sourceText.trim();
    
    if (searchQuery.length > 80) {
      // Try to find the most distinctive part of the text
      // Look for quoted text first
      const quotedMatch = searchQuery.match(/"([^"]{10,60})"/);
      if (quotedMatch) {
        searchQuery = quotedMatch[1];
      } else {
        // Look for sentences with specific terms that are likely unique
        const sentences = searchQuery.split(/[.!?]+/);
        for (const sentence of sentences) {
          const trimmed = sentence.trim();
          if (trimmed.length >= 20 && trimmed.length <= 80) {
            // Prefer sentences with numbers, percentages, or specific terms
            if (/\d|%|shall|must|will|within|during|before|after|pursuant|subject to/i.test(trimmed)) {
              searchQuery = trimmed;
              break;
            }
          }
        }
        
        // If no good sentence found, take a meaningful chunk
        if (searchQuery.length > 80) {
          // Find a good break point around the middle
          const midPoint = Math.min(60, searchQuery.length / 2);
          const beforeMid = searchQuery.substring(0, midPoint);
          const lastSpace = beforeMid.lastIndexOf(' ');
          const afterMid = searchQuery.substring(midPoint);
          const nextSpace = afterMid.indexOf(' ');
          
          if (lastSpace > 20 && nextSpace > 0) {
            searchQuery = searchQuery.substring(lastSpace + 1, midPoint + nextSpace);
          } else {
            // Fallback: take first meaningful chunk
            searchQuery = searchQuery.substring(0, 60);
            const lastSpaceFallback = searchQuery.lastIndexOf(' ');
            if (lastSpaceFallback > 20) {
              searchQuery = searchQuery.substring(0, lastSpaceFallback);
            }
          }
        }
      }
    }
    
    // Remove extra whitespace and clean up
    searchQuery = searchQuery.replace(/\s+/g, ' ').trim();
    
    console.log('Searching for:', searchQuery); // Debug log
    setPDFSearchText(searchQuery);
    setIsPDFModalOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'text-green-600 dark:text-green-400';
      case 'rejected': return 'text-red-600 dark:text-red-400';
      case 'unverified': return 'text-orange-600 dark:text-orange-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <AlertCircle className="w-4 h-4" />;
      case 'unverified': return <Clock className="w-4 h-4" />;
      default: return null;
    }
  };

  const getHighlightRange = (term: ExtractedTerm) => {
    if (term.source_start_char !== undefined && term.source_end_char !== undefined && documentText) {
      // Calculate relative position within the source text
      const sourceLength = term.source_text.length;
      return [{
        start: 0,
        end: sourceLength,
        className: 'bg-yellow-200 dark:bg-yellow-800 px-1 rounded'
      }];
    }
    return [];
  };

  const handleTermChange = (termId: string, newValue: string, status: ValueStatus) => {
    if (!analysisResult) return;

    // Update the local state for all term types
    const updateTermsInArray = <T extends ExtractedTerm>(terms: T[]): T[] =>
      terms.map(term => 
        term.id === termId 
          ? { ...term, validation_status: status }
          : term
      );

    setAnalysisResult(prev => ({
      ...prev!,
      commercial_terms: updateTermsInArray(prev!.commercial_terms),
      sla_terms: updateTermsInArray(prev!.sla_terms),
      performance_guarantees: updateTermsInArray(prev!.performance_guarantees),
      date_signing_terms: updateTermsInArray(prev!.date_signing_terms),
      risk_terms: updateTermsInArray(prev!.risk_terms),
      key_terms: prev!.key_terms
        ? prev!.key_terms.map((t: any) => (t.id === termId ? { ...t, validation_status: status, value: newValue } : t))
        : prev!.key_terms,
    }));

    onTermUpdate?.(termId, newValue, status);
  };

  const renderConfidenceBar = (confidence: number) => (
    <div className="flex items-center space-x-2">
      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div 
          className={`h-2 rounded-full ${
            confidence >= 0.8 ? 'bg-green-500' :
            confidence >= 0.6 ? 'bg-yellow-500' : 'bg-red-500'
          }`}
          style={{ width: `${confidence * 100}%` }}
        />
      </div>
      <span className="text-sm text-gray-600 dark:text-gray-400">
        {Math.round(confidence * 100)}%
      </span>
    </div>
  );

  const SourceTextDisplay: React.FC<{ sourceText: string }> = ({ sourceText }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-gray-600 dark:text-gray-400 font-medium text-sm">
          Source Text
        </label>
        {documentUrl && (
          <button
            onClick={() => handleGoToPage(sourceText)}
            className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
            title="Open PDF and find this text"
          >
            <ExternalLink className="w-3 h-3" />
            <span>Go to page</span>
          </button>
        )}
      </div>
      <div className="text-sm text-gray-700 dark:text-gray-300 p-3 bg-gray-50 dark:bg-gray-800 rounded border">
        {sourceText || '—'}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
        <span className="ml-3 text-gray-600 dark:text-gray-400">
          Loading extracted terms...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Error loading analysis
          </h3>
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="btn-primary"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analysisResult) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          No analysis results found
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          The document needs to be processed first. Try running the healthcare contract analysis.
        </p>
      </div>
    );
  }

  const totalTerms = 
    analysisResult.commercial_terms.length +
    analysisResult.sla_terms.length +
    analysisResult.performance_guarantees.length +
    analysisResult.date_signing_terms.length +
    analysisResult.risk_terms.length +
    (analysisResult.key_terms?.length || 0);

  // Build available tags for Key Terms and Insights
  const keyTermAvailableTags = Array.from(
    new Set(
      (analysisResult.key_terms || [])
        .map((t: any) => (t.kind || '').toString())
        .filter((x: string) => x && x !== 'risk')
    )
  ).sort();

  // Derive insights (risks) from key_terms if risk_terms is empty
  const derivedRiskItems = (analysisResult.key_terms || [])
    .filter((t: any) => (t.kind || '').toString() === 'risk')
    .map((t: any) => {
      const attrs: any = t.attributes || {};
      return {
        id: t.id || `${t.name}-risk`,
        risk_type: attrs.risk_type || 'risk',
        risk_category: t.category || attrs.risk_category || 'risk',
        risk_name: t.name || 'Risk',
        risk_description: t.value || attrs.risk_description || '',
        risk_severity: attrs.risk_severity,
        risk_probability: attrs.risk_probability,
        mitigation_measures: attrs.mitigation_measures,
        insurance_required: attrs.insurance_required,
        insurance_amount: attrs.insurance_amount,
        insurance_currency: attrs.insurance_currency,
        liability_cap_amount: attrs.liability_cap_amount,
        liability_cap_currency: attrs.liability_cap_currency,
        liability_cap_description: attrs.liability_cap_description,
        termination_trigger: attrs.termination_trigger,
        notice_period_days: attrs.notice_period_days,
        confidence_score: t.confidence_score || 0,
        source_text: t.source_text || '',
        source_start_char: t.source_start_char,
        source_end_char: t.source_end_char,
        extraction_note: t.extraction_note,
        validation_status: t.validation_status || 'unverified',
      } as RiskTerm;
    });

  const insightsAll: RiskTerm[] = analysisResult.risk_terms.length > 0 ? analysisResult.risk_terms : derivedRiskItems;

  const insightAvailableTags = Array.from(
    new Set(
      insightsAll.map((t) => (t.risk_category || 'risk').toString()).concat('risk')
    )
  ).sort();

  const isKeyTermTagSelected = (tag: string) => selectedKeyTermTags.includes(tag);
  const isInsightTagSelected = (tag: string) => selectedInsightTags.includes(tag);

  const toggleKeyTermTag = (tag: string) => {
    setSelectedKeyTermTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const toggleInsightTag = (tag: string) => {
    setSelectedInsightTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const resetKeyTermTags = () => setSelectedKeyTermTags([]);
  const resetInsightTags = () => setSelectedInsightTags([]);

  // Filtered data per selections (union semantics)
  const filteredKeyTerms = (analysisResult.key_terms || [])
    .filter((t: any) => (t.kind || '').toString() !== 'risk') // exclude risk from key terms
    .filter((t: any) =>
      selectedKeyTermTags.length === 0 ? true : selectedKeyTermTags.includes((t.kind || '').toString())
    );

  const filteredRiskTerms = insightsAll.filter((t) =>
    selectedInsightTags.length === 0
      ? true
      : selectedInsightTags.includes((t.risk_category || 'risk').toString()) ||
        selectedInsightTags.includes('risk')
  );

  if (totalTerms === 0) {
    return (
      <div className="text-center py-12">
        <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          No terms extracted yet
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Run the healthcare contract analysis to extract SLAs and Performance Guarantees.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header with summary */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Extracted Contract Terms
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {totalTerms} terms extracted • Review and validate AI findings
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Sub-Tabs: Key Terms / Insights */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setActiveTab('keyTerms')}
          className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
            activeTab === 'keyTerms'
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700'
          }`}
        >
          🧩 Key Terms ({filteredKeyTerms.length})
        </button>
        <button
          onClick={() => setActiveTab('insights')}
          className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
            activeTab === 'insights'
              ? 'bg-red-600 text-white border-red-600'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700'
          }`}
        >
          💡 Insights ({filteredRiskTerms.length})
        </button>
      </div>

      {/* Key Terms */}
      {activeTab === 'keyTerms' && analysisResult.key_terms && filteredKeyTerms.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-indigo-500" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Key Terms ({filteredKeyTerms.length})
            </h4>
          </div>
          {keyTermAvailableTags.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              {keyTermAvailableTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleKeyTermTag(tag)}
                  className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                    isKeyTermTagSelected(tag)
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700'
                  }`}
                >
                  {formatTagLabel(tag, KEY_TERM_TAG_LABELS)}
                </button>
              ))}
              {selectedKeyTermTags.length > 0 && (
                <button
                  onClick={resetKeyTermTags}
                  className="ml-2 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Reset
                </button>
              )}
            </div>
          )}
          <div className="space-y-4">
            {filteredKeyTerms.map((t, idx) => {
              const termId = t.id || `term-${idx}`;
              const isCollapsed = collapsedTerms.has(termId);
              const isRemoved = t.status === 'removed';
              const attrs: Record<string, any> | undefined = (t as any).attributes;
              const attributeEntries = attrs
                ? Object.entries(attrs).filter(([_, v]) => v !== null && v !== undefined && String(v).trim() !== '')
                : [];
              return (
                <div key={termId} className={`card ${isRemoved ? 'opacity-75' : ''}`}>
                  <div className="card-header cursor-pointer" onClick={() => toggleTermCollapse(termId)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <button
                          className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTermCollapse(termId);
                          }}
                        >
                          {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                        </button>
                        <h5 className={`font-medium text-gray-900 dark:text-gray-100 ${isRemoved ? 'line-through' : ''}`}>
                          {t.name || t.category || t.kind}
                        </h5>
                        {getStatusBadge(t.status)}
                      </div>
                      <div className={`flex items-center space-x-1 text-sm ${getStatusColor(t.validation_status || 'unverified')}`}>
                        {getStatusIcon(t.validation_status || 'unverified')}
                        <span className="capitalize">{t.validation_status || 'unverified'}</span>
                      </div>
                    </div>
                  </div>
                  {!isCollapsed && (
                  <div className="space-y-4">
                    <AIValidatedInputWithHighlight
                      value={t.value || ''}
                      initialValueStatus={(t.validation_status || 'unverified') as ValueStatus}
                      valueContext={t.source_text || ''}
                      highlightRanges={getHighlightRange(t)}
                      extractionNote={t.extraction_note}
                      label={`${t.kind?.toUpperCase() || 'TERM'} Value`}
                      onChange={(newValue, status) => handleTermChange(t.id || `${idx}`, newValue, status)}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                          Kind
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{formatTagLabel((t.kind || '').toString(), KEY_TERM_TAG_LABELS)}</p>
                      </div>
                      <div>
                        <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                          Confidence
                        </label>
                        {renderConfidenceBar(t.confidence_score || 0)}
                      </div>
                    </div>
                    <SourceTextDisplay sourceText={t.source_text || ''} />
                    
                    {attributeEntries.length > 0 && (
                      <div className="space-y-2">
                        <label className="block text-gray-600 dark:text-gray-400 font-medium">
                          Additional Info
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {attributeEntries.map(([key, value]) => (
                            <div key={key} className="p-2 rounded border border-gray-200 dark:border-gray-700">
                              <div className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">
                                {key.replace(/_/g, ' ')}
                              </div>
                              <div className="text-gray-900 dark:text-gray-100 break-words">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              )})}
          </div>
        </div>
      )}

      {/* SLA Terms */}
      {analysisResult.sla_terms.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-blue-500" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Service Level Agreements ({analysisResult.sla_terms.length})
            </h4>
          </div>
          
          <div className="space-y-4">
            {analysisResult.sla_terms.map((term) => {
              let meta: any = {};
              try { meta = term.extraction_note ? JSON.parse(term.extraction_note) : {}; } catch {}
              const performanceCriteria = meta.performance_criteria || term.sla_value;
              const measurementPeriod = meta.measurement_period || '';
              const atRiskPercent = typeof meta.at_risk_percent === 'number' ? meta.at_risk_percent : undefined;
              return (
              <div key={term.id} className="card">
                <div className="card-header">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100">
                      {term.sla_name}
                    </h5>
                    <div className={`flex items-center space-x-1 text-sm ${getStatusColor(term.validation_status)}`}>
                      {getStatusIcon(term.validation_status)}
                      <span className="capitalize">{term.validation_status}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">Name</label>
                      <p className="text-gray-900 dark:text-gray-100">{term.sla_name || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">Measurement Period</label>
                      <p className="text-gray-900 dark:text-gray-100">{measurementPeriod || '—'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <AIValidatedInputWithHighlight
                        value={performanceCriteria}
                        initialValueStatus={term.validation_status as ValueStatus}
                        valueContext={term.source_text}
                        highlightRanges={getHighlightRange(term)}
                        extractionNote={term.extraction_note}
                        label="Performance Criteria"
                        onChange={(newValue, status) => handleTermChange(term.id, newValue, status)}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">At-Risk %</label>
                      <p className="text-gray-900 dark:text-gray-100">{typeof atRiskPercent === 'number' ? `${atRiskPercent}%` : '—'}</p>
                    </div>
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">Confidence</label>
                      {renderConfidenceBar(term.confidence_score)}
                    </div>
                  </div>
                  <SourceTextDisplay sourceText={term.source_text || ''} />
                </div>
              </div>
            )})}
          </div>
        </div>
      )}

      {/* Performance Guarantees */}
      {analysisResult.performance_guarantees.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Target className="w-5 h-5 text-green-500" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Performance Guarantees ({analysisResult.performance_guarantees.length})
            </h4>
          </div>
          
          <div className="space-y-4">
            {analysisResult.performance_guarantees.map((term) => (
              <div key={term.id} className="card">
                <div className="card-header">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100">
                      {term.pg_name}
                    </h5>
                    <div className={`flex items-center space-x-1 text-sm ${getStatusColor(term.validation_status)}`}>
                      {getStatusIcon(term.validation_status)}
                      <span className="capitalize">{term.validation_status}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <AIValidatedInputWithHighlight
                    value={term.target_value}
                    initialValueStatus={term.validation_status as ValueStatus}
                    valueContext={term.source_text}
                    highlightRanges={getHighlightRange(term)}
                    extractionNote={term.extraction_note}
                    label="Performance Target"
                    onChange={(newValue, status) => handleTermChange(term.id, newValue, status)}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                        Type
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{term.pg_type}</p>
                    </div>
                    
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                        Metric
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{term.performance_metric}</p>
                    </div>
                    
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                        Confidence
                      </label>
                      {renderConfidenceBar(term.confidence_score)}
                    </div>
                    
                    {term.measurement_period && (
                      <div>
                        <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                          Measurement Period
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{term.measurement_period}</p>
                      </div>
                    )}
                    
                    {term.penalty_description && (
                      <div className="md:col-span-3">
                        <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                          Penalties
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">
                          {term.penalty_description}
                          {term.penalty_amount && term.penalty_currency && (
                            <span className="ml-2 font-semibold">
                              ({term.penalty_amount.toLocaleString()} {term.penalty_currency})
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                    
                    {term.remedy_description && (
                      <div className="md:col-span-3">
                        <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                          Remedies
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{term.remedy_description}</p>
                      </div>
                    )}
                  </div>
                  <SourceTextDisplay sourceText={term.source_text || ''} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Commercial Terms */}
      {analysisResult.commercial_terms.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Commercial Terms ({analysisResult.commercial_terms.length})
            </h4>
          </div>
          
          <div className="space-y-4">
            {analysisResult.commercial_terms.map((term) => (
              <div key={term.id} className="card">
                <div className="card-header">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100">
                      {term.term_name}
                    </h5>
                    <div className={`flex items-center space-x-1 text-sm ${getStatusColor(term.validation_status)}`}>
                      {getStatusIcon(term.validation_status)}
                      <span className="capitalize">{term.validation_status}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <AIValidatedInputWithHighlight
                    value={term.term_value}
                    initialValueStatus={term.validation_status as ValueStatus}
                    valueContext={term.source_text}
                    highlightRanges={getHighlightRange(term)}
                    extractionNote={term.extraction_note}
                    label="Commercial Value"
                    onChange={(newValue, status) => handleTermChange(term.id, newValue, status)}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                        Type
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{term.term_type}</p>
                    </div>
                    
                    {term.monetary_amount && (
                      <div>
                        <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                          Amount
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">
                          {term.monetary_amount.toLocaleString()} {term.currency}
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                        Confidence
                      </label>
                      {renderConfidenceBar(term.confidence_score)}
                    </div>
                  </div>
                  <SourceTextDisplay sourceText={term.source_text || ''} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights (e.g., Risks) */}
      {activeTab === 'insights' && insightsAll.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-red-500" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Insights ({filteredRiskTerms.length})</h4>
          </div>
          {insightAvailableTags.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              {insightAvailableTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleInsightTag(tag)}
                  className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                    isInsightTagSelected(tag)
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700'
                  }`}
                >
                  {formatTagLabel(tag, INSIGHT_TAG_LABELS)}
                </button>
              ))}
              {selectedInsightTags.length > 0 && (
                <button
                  onClick={resetInsightTags}
                  className="ml-2 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Reset
                </button>
              )}
            </div>
          )}
          
          <div className="space-y-4">
            {filteredRiskTerms.map((term) => (
              <div key={term.id} className="card">
                <div className="card-header">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100">
                      {term.risk_name}
                    </h5>
                    <div className={`flex items-center space-x-1 text-sm ${getStatusColor(term.validation_status)}`}>
                      {getStatusIcon(term.validation_status)}
                      <span className="capitalize">{term.validation_status}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <AIValidatedInputWithHighlight
                    value={term.risk_description}
                    initialValueStatus={term.validation_status as ValueStatus}
                    valueContext={term.source_text}
                    highlightRanges={getHighlightRange(term)}
                    extractionNote={term.extraction_note}
                    label="Risk Description"
                    onChange={(newValue, status) => handleTermChange(term.id, newValue, status)}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                        Category
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{term.risk_category}</p>
                    </div>
                    
                    {term.risk_severity && (
                      <div>
                        <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                          Severity
                        </label>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          term.risk_severity === 'high' ? 'bg-red-100 text-red-800' :
                          term.risk_severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {term.risk_severity}
                        </span>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                        Confidence
                      </label>
                      {renderConfidenceBar(term.confidence_score)}
                    </div>
                  </div>
                  <SourceTextDisplay sourceText={term.source_text || ''} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Date/Signing Terms */}
      {analysisResult.date_signing_terms.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-purple-500" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Dates & Signing ({analysisResult.date_signing_terms.length})
            </h4>
          </div>
          
          <div className="space-y-4">
            {analysisResult.date_signing_terms.map((term) => (
              <div key={term.id} className="card">
                <div className="card-header">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100">
                      {term.date_name}
                    </h5>
                    <div className={`flex items-center space-x-1 text-sm ${getStatusColor(term.validation_status)}`}>
                      {getStatusIcon(term.validation_status)}
                      <span className="capitalize">{term.validation_status}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <AIValidatedInputWithHighlight
                    value={term.date_text || ''}
                    initialValueStatus={term.validation_status as ValueStatus}
                    valueContext={term.source_text}
                    highlightRanges={getHighlightRange(term)}
                    extractionNote={term.extraction_note}
                    label="Date Information"
                    onChange={(newValue, status) => handleTermChange(term.id, newValue, status)}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                        Type
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{term.date_type}</p>
                    </div>
                    
                    <div>
                      <label className="block text-gray-600 dark:text-gray-400 font-medium mb-1">
                        Confidence
                      </label>
                      {renderConfidenceBar(term.confidence_score)}
                    </div>
                  </div>
                  <SourceTextDisplay sourceText={term.source_text || ''} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {documentUrl && (
        <PDFViewerModal
          isOpen={isPDFModalOpen}
          onClose={() => setIsPDFModalOpen(false)}
          url={documentUrl}
          filename={documentFilename}
          searchText={pdfSearchText}
        />
      )}
    </div>
  );
}; 